import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  disabled?: boolean;
  inDialog?: boolean;
  "data-testid"?: string;
}

const SENTINEL_VALUES = new Set(["none", "unassigned", "all", "inherit", "skip"]);

/** Walk up the DOM and find the nearest Radix dialog/sheet content pane.
 *  Returns it so we can portal INTO it (keeps us inside the focus trap)
 *  rather than into document.body (which is outside the focus trap). */
function findModalContainer(el: HTMLElement): HTMLElement {
  let cur: HTMLElement | null = el.parentElement;
  while (cur) {
    const role = cur.getAttribute("role");
    const tag = cur.tagName;
    // Radix Dialog / Sheet / AlertDialog content elements carry role="dialog"
    if (role === "dialog" || role === "alertdialog") return cur;
    // Fallback: a fixed full-screen container (sheet)
    if (tag !== "BODY" && tag !== "HTML") {
      const s = window.getComputedStyle(cur);
      if (s.position === "fixed" && s.zIndex !== "auto" && parseInt(s.zIndex) >= 40) {
        return cur;
      }
    }
    cur = cur.parentElement;
  }
  return document.body;
}

/** Returns true only if the element has a non-identity CSS transform that
 *  creates a new "fixed positioning containing block" (per CSS spec). */
function hasTransformContext(el: HTMLElement): boolean {
  const s = window.getComputedStyle(el);
  const t = s.transform;
  if (!t || t === "none" || t === "matrix(1, 0, 0, 1, 0, 0)") return false;
  const f = s.filter;
  if (f && f !== "none") return true;
  return true; // any non-identity transform qualifies
}

/** Given a trigger element and the portal container, compute the
 *  position:fixed coordinates for the dropdown.
 *
 *  - document.body (no transform) → use raw viewport coords.
 *  - Container WITH a CSS transform (e.g. Dialog's translate-50%) →
 *    position:fixed is relative to that container, so subtract its rect. */
function calcPos(
  triggerEl: HTMLElement,
  container: HTMLElement
): { top: number; left: number; width: number } {
  const tr = triggerEl.getBoundingClientRect();
  if (container === document.body || !hasTransformContext(container)) {
    return { top: tr.bottom + 4, left: tr.left, width: tr.width };
  }
  const cr = container.getBoundingClientRect();
  return {
    top: tr.bottom - cr.top + 4,
    left: tr.left - cr.left,
    width: tr.width,
  };
}

export function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found",
  className,
  disabled,
  "data-testid": testId,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLElement>(document.body);

  const sorted = useMemo(() => {
    const sentinels = options.filter((o) => SENTINEL_VALUES.has(o.value));
    const rest = options
      .filter((o) => !SENTINEL_VALUES.has(o.value))
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    return [...sentinels, ...rest];
  }, [options]);

  const filtered = useMemo(() => {
    if (!search.trim()) return sorted;
    const q = search.toLowerCase();
    return sorted.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [sorted, search]);

  const selectedLabel = sorted.find((o) => o.value === value)?.label;

  const measure = useCallback(() => {
    if (triggerRef.current) {
      const container = containerRef.current;
      setPos(calcPos(triggerRef.current, container));
    }
  }, []);

  // Resolve the portal container once on mount
  useEffect(() => {
    if (triggerRef.current) {
      containerRef.current = findModalContainer(triggerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setSearch("");
      return;
    }
    // Re-resolve container (dialog might have opened after mount)
    if (triggerRef.current) {
      containerRef.current = findModalContainer(triggerRef.current);
    }
    measure();
    // Focus the search input
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, measure]);

  // Close on outside mousedown
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      // Keep open if clicking the trigger or the dropdown itself
      if (triggerRef.current?.contains(target)) return;
      if ((e.target as HTMLElement).closest?.("[data-searchable-dropdown]")) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [open]);

  function select(val: string) {
    onValueChange(val);
    setOpen(false);
    triggerRef.current?.focus();
  }

  const dropdown = open
    ? createPortal(
        <div
          data-searchable-dropdown
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: pos.width,
            minWidth: 180,
            zIndex: 99999,
          }}
          className="rounded-md border bg-popover text-popover-foreground shadow-lg"
          // Prevent the dialog from treating this click as "outside"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="p-2">
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-sm border border-input bg-background px-3 py-1.5 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
              onKeyDown={(e) => {
                if (e.key === "Escape") { setOpen(false); triggerRef.current?.focus(); }
                if (e.key === "Enter" && filtered.length > 0) select(filtered[0].value);
                e.stopPropagation();
                e.nativeEvent?.stopImmediatePropagation?.();
              }}
            />
          </div>
          <div className="max-h-60 overflow-y-auto pb-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">{emptyText}</p>
            ) : (
              filtered.map((option) => (
                <div
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    select(option.value);
                  }}
                >
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0 text-primary",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>,
        containerRef.current
      )
    : null;

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        disabled={disabled}
        className={cn("justify-between font-normal", className)}
        onClick={() => { measure(); setOpen((o) => !o); }}
        data-testid={testId}
      >
        <span className="truncate">{selectedLabel || placeholder}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {dropdown}
    </>
  );
}
